import { useSearchParams } from "react-router"

const SAFE_RETURN_TO_PREFIX = "/material-compliance/"
// Any fixed origin does: it only exists so a relative path can be parsed, and
// so a value that smuggles in a host ("//evil.example.com", "/\evil.example.com")
// shows up as a changed origin.
const PARSE_BASE = "http://return-to.invalid"

/**
 * Validates a raw `return_to` value: root-relative, and still inside
 * `/material-compliance/` AFTER dot segments are resolved. A bare prefix check
 * accepts "/material-compliance/../other-app", which the router then resolves
 * outside the app. Returns the normalized path (with query and hash) or null.
 */
export function resolveReturnTo(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/")) return null
  let url: URL
  try {
    url = new URL(raw, PARSE_BASE)
  } catch {
    return null
  }
  if (url.origin !== PARSE_BASE) return null
  if (!url.pathname.startsWith(SAFE_RETURN_TO_PREFIX)) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Reads `return_to` from the URL. Returns it only when it is a same-app
 * relative path inside `/material-compliance/` (see resolveReturnTo), never
 * an absolute URL or a path outside this app, so a shared link can't become an
 * open redirect (TUR-1539 D5, spec §3.7's "fixing data returns to the statement").
 */
export function useReturnTo(): string | null {
  const [searchParams] = useSearchParams()
  return resolveReturnTo(searchParams.get("return_to"))
}
